import { useAtom } from "jotai";
import { providerLocationsAtom } from "./analysisAtoms";
import { useQuery } from "@tanstack/react-query";
import { mbxUserAtom } from "../../atoms/atoms";
import { startOfDay, endOfDay } from "date-fns";
import { execReadBySortkey } from "../../modules/DBService";
import { useServices } from "./useServices";
import { format } from "date-fns";
import { pointToPolygonDistance } from "@turf/point-to-polygon-distance";
import { point, polygon } from "@turf/helpers";

const listProspects = /* GraphQL */ `
    query ListProspects(
        $companyId: ID
        $prospectId: ModelStringKeyConditionInput
        $filter: ModelProspectFilterInput
        $limit: Int
        $nextToken: String
        $sortDirection: ModelSortDirection
    ) {
        listProspects(
            companyId: $companyId
            prospectId: $prospectId
            filter: $filter
            limit: $limit
            nextToken: $nextToken
            sortDirection: $sortDirection
        ) {
            items {
                prospectId
                emailaddress
                firstname
                lastname
                phone
                addressoneline
                street
                city
                state
                country
                postalcode
                longitude
                latitude
                pageUrl
                SearchParamsInfo
                createdAt
                updatedAt
            }
            nextToken
        }
    }
`;
const now = new Date();
function removeNullOrEmptyNestedArrays(arr) {
    if (!Array.isArray(arr)) {
        return arr;
    }

    return arr.filter((nestedArr) => {
        if (!Array.isArray(nestedArr)) {
            return nestedArr !== null;
        }

        return nestedArr.length > 0;
    });
}
export function useWaitList() {
    const dateRange = {
        start: new Date(new Date(now).setDate(now.getDate() - 90)),
        end: new Date(now.getFullYear(), now.getMonth(), now.getDate())
    };
    const { data: services } = useServices();
    const [mbxUser] = useAtom(mbxUserAtom);
    const [providerLocations] = useAtom(providerLocationsAtom);

    function hasAtleastOneLiveSB(sbs) {
        let result = false;
        const now = new Date();
        for (let sb of sbs) {
            const endDate = endOfDay(new Date(sb.endDate));
            result = endDate >= now;
            if (result) break;
        }
        return result;
    }
    return useQuery({
        queryKey: ["analysis-waitlist", dateRange],
        queryFn: async () => {
            try {
                const { items: prospects } = await execReadBySortkey({
                    opname: "listProspects",
                    op: listProspects,
                    id: {
                        companyId: mbxUser.companyId
                    },
                    limit: 500,
                    sortDirection: "ASC",
                    skey: {
                        prospectId: {
                            between: [
                                startOfDay(dateRange.start).getTime(),
                                endOfDay(dateRange.end).getTime()
                            ]
                        }
                    }
                });
                const locationsWithLiveSBs = providerLocations.filter(
                    (locations) => hasAtleastOneLiveSB(locations.SBs)
                );
                const travelZonePointsMap = new Map();
                for (let prospect of prospects) {
                    try {
                        try {
                            if (prospect.longitude && prospect.latitude) {
                                const candidateTravelZones =
                                    locationsWithLiveSBs.filter(
                                        (location) =>
                                            location.state &&
                                            prospect.state &&
                                            location.state === prospect.state
                                    ); // only get the travel zones of the same state/province as the waitlist item
                                prospect.distance = 0;
                                prospect.insideTravelZoneMessages = [];
                                const uniqueInsideProviderIds = new Set();
                                for (const providerLocation of candidateTravelZones) {
                                    if (
                                        providerLocation.travelregions &&
                                        providerLocation.travelregions.length
                                    ) {
                                        let travelZonePoints =
                                            travelZonePointsMap.get(
                                                providerLocation.id
                                            );
                                        if (!travelZonePoints) {
                                            travelZonePoints =
                                                providerLocation.travelregions
                                                    .filter(
                                                        (zone) =>
                                                            !!zone &&
                                                            zone?.length >= 3
                                                    )
                                                    .map((zone) => {
                                                        const zonePoints =
                                                            zone.map(
                                                                (zpoint) => [
                                                                    zpoint.lng,
                                                                    zpoint.lat
                                                                ]
                                                            );
                                                        zonePoints.push([
                                                            zone[0].lng,
                                                            zone[0].lat
                                                        ]);

                                                        return zonePoints;
                                                    });
                                            travelZonePoints =
                                                removeNullOrEmptyNestedArrays(
                                                    travelZonePoints
                                                );
                                            travelZonePointsMap.set(
                                                providerLocation.id,
                                                travelZonePoints
                                            );
                                        }

                                        if (travelZonePoints?.length > 0)
                                            try {
                                                for (let aPolygon of travelZonePoints) {
                                                    const distance =
                                                        pointToPolygonDistance(
                                                            point([
                                                                prospect.longitude,
                                                                prospect.latitude
                                                            ]),
                                                            polygon([aPolygon])
                                                        );
                                                    if (
                                                        Math.sign(distance) ===
                                                            -1 &&
                                                        !uniqueInsideProviderIds.has(
                                                            providerLocation.providerId
                                                        )
                                                    ) {
                                                        uniqueInsideProviderIds.add(
                                                            providerLocation.providerId
                                                        );
                                                        if (
                                                            prospect
                                                                .insideTravelZoneMessages
                                                                ?.length < 5
                                                        )
                                                            prospect.insideTravelZoneMessages.push(
                                                                `Inside travel zone of ${providerLocation.firstname} ${providerLocation.lastname}`
                                                            );
                                                    }
                                                    if (
                                                        Math.sign(distance) ===
                                                            1 &&
                                                        (prospect.distance ===
                                                            0 ||
                                                            distance <
                                                                prospect.distance)
                                                    ) {
                                                        prospect.distance =
                                                            distance.toFixed(1);
                                                        prospect.nearestProviderMessage = `${prospect.distance} km away from the travel zone of ${providerLocation.firstname} ${providerLocation.lastname}`;
                                                    }
                                                }
                                            } catch (e) {
                                                console.log(
                                                    "error in pointToPolygonDistance",
                                                    e,
                                                    travelZonePoints,
                                                    providerLocation
                                                );
                                            }
                                    }
                                }
                            }
                        } catch (e) {
                            console.log(
                                "error while finding travel zone near waitlist",
                                e
                            );
                        }
                        prospect.formattedCreatedAt = format(
                            new Date(prospect.createdAt),
                            "MMM dd, yyyy – h:mm a"
                        );
                        try {
                            const searchParamsInfo = JSON.parse(
                                prospect.SearchParamsInfo
                            );
                            const servicesIdsArray =
                                searchParamsInfo?.services || [];

                            prospect.serviceNames = [];
                            for (let serviceId of servicesIdsArray) {
                                const serviceName = services.find(
                                    (service) => service.id === serviceId
                                )?.name;
                                if (serviceName) {
                                    prospect.serviceNames.push(serviceName);
                                }
                            }
                        } catch (e) {
                            console.log("SearchParamsInfo parsing error", e);
                        }
                    } catch (e) {
                        console.log("SearchParamsInfo parsing error", e);
                    }
                }
                return prospects.filter(
                    (prospect) => prospect.latitude && prospect.longitude
                );
            } catch (e) {
                console.log("error bookings-bks", e);
                return [];
            }
        },
        enabled:
            services &&
            services.length &&
            providerLocations &&
            providerLocations.length
                ? true
                : false
    });
}
